// The journey of one configuration change (lane B10 S20a; plan D14; CR-066 "Change -> Validate -> Version -> Activate ->
// Audit" · "Given/When/Then: historical records retain configuration version used."). PARALLEL-MAP §2.1 freezes
// `<Journey kind configKey body onDone />` for S20b's editors: the caller's editor is the Change step and hands over the
// changed body; the Journey runs the rest as one slide-over, one tool per step:
//   Validate  `config.validate` — a read that writes nothing: every rule `config.propose` would apply, each issue named,
//             and the change against the version in effect drawn as a Diff (Old → New), entry by entry for a list;
//   Version   the reason and the date it takes effect, then `config.propose` — a draft numbered after every earlier
//             version, receipted;
//   Activate  a second Admin's *Approve* (`config.approve`, on the area's draft card): never the proposer's own
//             (`SOD_CONFLICT`);
//   Audit     the receipts of both steps, and the version id every later record stamps (*Recorded · audit event*).
// The proposer's journey ends at Activate: the draft waits for the other Admin. No hotfix is offered here — a version
// takes effect from the next business day, as `config.propose` requires without one.

import { useState, type ReactNode } from "react";
import { configPropose, configValidate, type ConfigProposeOutput } from "@/api/tools";
import { businessLocalNow, businessLocalToISO, type AppError } from "@/api/runtime";
import { isClientRefusal, useRead, useWrite } from "@/api/useTool";
import { useSession } from "@/session/useSession";
import {
  ACTION_LABELS, Banner, Button, COMMON_LABELS, CORE_LABELS, DEFAULT_TIME_ZONE, Diff, ErrorStrip, G1_LABELS, HISTORY_LABELS, Input,
  JOURNEY_LABELS, Journey as Steps, KeyValue, Lines, PANEL_CHANGE_STATUS, RECO_LABELS, RULE_LABELS, Recorded, SURFACE_LABELS, Skeleton,
  SlideOver, Stack, TextArea, VERSION_TEMPLATES, formatDateTime, type DiffRow, type JourneyStep,
} from "@/mc";
import { CONSOLE_LABELS, keyWord } from "./labels";
import { entryChanges, listField, type Body } from "./ListEditor";
import { hsKey } from "@/views/b10/lib/hsKey";

// ---- the five steps -----------------------------------------------------------------------------------------------------

export const JOURNEY_STAGES = ["change", "validate", "version", "activate", "audit"] as const;
export type JourneyStageKey = (typeof JOURNEY_STAGES)[number];
/** The step in hand; `done` once the Audit step is recorded too. */
export type JourneyStage = JourneyStageKey | "done";

/** The five tiles for the step in hand: earlier ones done, this one active, later ones next. */
export function journeySteps(stage: JourneyStage, subs: Partial<Record<JourneyStageKey, ReactNode>> = {}): JourneyStep[] {
  const at = stage === "done" ? JOURNEY_STAGES.length : JOURNEY_STAGES.indexOf(stage);
  return JOURNEY_STAGES.map((key, index): JourneyStep => ({
    label: JOURNEY_LABELS[key],
    state: index < at ? "done" : index === at ? "active" : "next",
    sub: subs[key],
    hs: hsKey("cfg.step", key),
  }));
}

/** The strip of the five tiles (D0 Journey, compact). */
export function JourneyStrip({ stage, subs }: { stage: JourneyStage; subs?: Partial<Record<JourneyStageKey, ReactNode>> | undefined }) {
  return <Steps steps={journeySteps(stage, subs)} label={CONSOLE_LABELS.configurationVersion} sm />;
}

// ---- the date a version takes effect ------------------------------------------------------------------------------------

const pad = (n: number) => String(n).padStart(2, "0");

/** The `datetime-local` value a version is dated from by default: the next weekday (Mon–Fri) after `local`, half an
 * hour later in the day than `local` (at most 23:59) — on or after `config.propose`'s earliest instant, the next
 * business day at this time, on an ordinary week; a holiday is Validate's to flag. "" when `local` is not a value. */
export function defaultActivation(local: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(local);
  if (!m) return "";
  const year = Number(m[1]);
  const month = Number(m[2]);
  const date = Number(m[3]);
  const minutes = Math.min(Number(m[4]) * 60 + Number(m[5]) + 30, 23 * 60 + 59);
  let day = new Date(Date.UTC(year, month - 1, date));
  do day = new Date(day.getTime() + 86_400_000);
  while (day.getUTCDay() === 0 || day.getUTCDay() === 6);
  return `${day.getUTCFullYear()}-${pad(day.getUTCMonth() + 1)}-${pad(day.getUTCDate())}T${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;
}

// ---- the change as Old → New ----------------------------------------------------------------------------------------------

const shown = (value: unknown): string =>
  value === null || value === undefined ? "—" : typeof value === "string" ? value : JSON.stringify(value);

/** `config.validate`'s / `config.open`'s field diff (`{path: {before, after}}`) as Diff rows, one per changed path. */
export function pathRows(diff: Record<string, unknown> | null | undefined): DiffRow[] {
  return Object.entries(diff ?? {}).map(([path, change]) => {
    const pair = typeof change === "object" && change !== null ? (change as { before?: unknown; after?: unknown }) : {};
    return { label: path, old: shown(pair.before), new: shown(pair.after), changed: true };
  });
}

/** The change as Diff rows: entry by entry for a list whose version in effect is known, else by field path. */
export function changeRows(kind: string, previous: Body | null | undefined, body: Body, diff: Record<string, unknown> | null | undefined): DiffRow[] {
  if (listField(kind) && previous !== undefined) {
    return entryChanges(kind, previous, body).map((c) => ({ label: c.name, old: c.old ?? "—", new: c.new ?? "—", changed: true }));
  }
  return pathRows(diff);
}

// ---- a refusal and a failure, as DC-24 draws them -------------------------------------------------------------------------

/** Refused (the server said no after the act): the red banner with the server's sentence and its code. A failure of the
 * system is the error strip with Retry. Nothing for no error. */
export function Refusal({ error, onRetry }: { error: AppError | null | undefined; onRetry?: (() => void) | undefined }) {
  if (!error) return null;
  if (isClientRefusal(error)) {
    return (
      <Banner tone="red" live right={error.code ? <span className="mc-mono">{error.code}</span> : undefined}>
        {error.remedy || error.message}
      </Banner>
    );
  }
  return (
    <ErrorStrip
      message={RULE_LABELS.failedSyncRetryable}
      problem={{ status: error.status, refusal_code: error.code, detail: error.message, request_id: error.requestId }}
      onRetry={onRetry}
      
    />
  );
}

// ---- the Journey -----------------------------------------------------------------------------------------------------------

export interface JourneyProps {
  kind: string;
  configKey: string;
  /** The changed body: what the Change step produced. */
  body: Body;
  /** Closed: with the draft it proposed, or null when it proposed nothing. */
  onDone: (proposal: ConfigProposeOutput | null) => void;
  /** Back to the Change step, the body kept (*Edit*). */
  onBack?: (() => void) | undefined;
  /** The list's name for the title (default: its word, else the key). */
  label?: string | undefined;
  /** The body of the version in effect, so a list's change is drawn entry by entry; absent, by field path. */
  previous?: Body | null | undefined;
}

export function Journey({ kind, configKey, body, onDone, onBack, label, previous }: JourneyProps) {
  const { me } = useSession();
  const timeZone = me?.timezone || DEFAULT_TIME_ZONE;
  const title = label ?? keyWord(configKey) ?? configKey;
  const [activateAt, setActivateAt] = useState(() => defaultActivation(businessLocalNow()));
  const [reason, setReason] = useState("");
  const effective = businessLocalToISO(activateAt);
  const payload = JSON.stringify(body);
  const check = useRead(
    ["config-validate", configKey, payload, effective],
    () => configValidate({ kind, key: configKey, payload: body, effective_from: effective || null }),
    { staleTime: 0 },
  );
  const propose = useWrite(configPropose, [["config"]]);
  const proposed = propose.data;

  const issues = check.data?.issues ?? [];
  const changed = Object.keys(check.data?.diff ?? {}).length > 0;
  const rows = check.data ? changeRows(kind, previous, body, check.data.diff) : [];
  const blockedReason = !check.data
    ? COMMON_LABELS.required
    : issues[0]?.detail ?? (!changed ? CORE_LABELS.nothingNeedsAction : reason.trim().length < 3 || !effective ? COMMON_LABELS.required : null);
  const submit = () => {
    if (blockedReason !== null || propose.isPending) return;
    propose.mutate({ kind, key: configKey, payload: body, effective_from: effective, note: reason.trim() });
  };

  if (proposed) {
    const version = VERSION_TEMPLATES.version({ n: proposed.version });
    return (
      <SlideOver
        title={title}
        onClose={() => onDone(proposed)}
        dismissLabel={RECO_LABELS.close}
        
      >
        <Stack gap={14}>
          <JourneyStrip stage="activate" subs={{ version, activate: PANEL_CHANGE_STATUS.draft }} />
          <Recorded live right={proposed.receipt_id != null ? <span className="mc-mono">{`#${proposed.receipt_id}`}</span> : undefined}>
            {title} <span className="mc-mono">{version}</span> · {PANEL_CHANGE_STATUS.draft}
          </Recorded>
          <KeyValue
            rows
            items={[
              [CONSOLE_LABELS.configurationVersion, <span className="mc-mono">{version}</span>],
              [HISTORY_LABELS.actor, proposed.published_by_name ?? proposed.published_by_id ?? "—"],
              [HISTORY_LABELS.timestamp, proposed.published_at ? formatDateTime(proposed.published_at, timeZone) : "—"],
              [JOURNEY_LABELS.activate, formatDateTime(proposed.effective_from, timeZone)],
              [HISTORY_LABELS.reason, proposed.note ?? "—"],
            ]}
          />
        </Stack>
      </SlideOver>
    );
  }

  const stage: JourneyStage = check.data?.valid && changed ? "version" : "validate";
  return (
    <SlideOver
      title={title}
      onClose={() => onDone(null)}
      dismissLabel={ACTION_LABELS.cancel}
      actions={onBack ? (
        <Button tone="ghost" icon="back" onClick={onBack} >{SURFACE_LABELS.edit}</Button>
      ) : undefined}
      primary={{
        label: ACTION_LABELS.submit,
        onClick: submit,
        blocked: blockedReason !== null ? { reason: blockedReason } : undefined,
        busy: propose.isPending,
        hs: "cfg.submit",
      }}
      hint={G1_LABELS.reasonStored}
      
    >
      <Stack gap={14}>
        <JourneyStrip stage={stage} />
        <Refusal error={propose.error} />
        <Refusal error={check.error} onRetry={() => void check.refetch()} />
        {check.isPending ? <Skeleton lines={3} /> : null}
        {issues.length > 0 ? (
          <Banner tone="red" live>
            <Lines
              plain
              items={issues.map((issue) => (
                <>
                  {issue.detail} <span className="mc-mono">{issue.field}</span>
                </>
              ))}
            />
          </Banner>
        ) : null}
        {check.data && rows.length > 0 ? <Diff form="cols" rows={rows} label={title} /> : null}
        {check.data && !changed ? <Banner tone="neutral">{CORE_LABELS.nothingNeedsAction}</Banner> : null}
        <Input
          type="datetime-local"
          label={JOURNEY_LABELS.activate}
          value={activateAt}
          onChange={(event) => setActivateAt(event.target.value)}
          required
          
        />
        <TextArea
          label={HISTORY_LABELS.reason}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          rows={3}
          required
          
        />
      </Stack>
    </SlideOver>
  );
}
